import React, { createContext, useContext, useState, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const getDesignTokens = (mode) => ({
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          primary: {
            main: '#0D6EFD',       // ShellKode primary blue
            light: '#EBF3FF',
            dark: '#0A58CA',
            contrastText: '#ffffff',
          },
          secondary: {
            main: '#00B4D8',       // ShellKode cyan accent
            light: '#E0F7FC',
            dark: '#0096C7',
          },
          background: {
            default: '#F5F7FA',
            paper: '#FFFFFF',
            secondary: '#F0F4F8',
          },
          text: {
            primary: '#0D1B3E',    // ShellKode dark navy
            secondary: '#5A6A85',
            disabled: '#9AAABB',
          },
          divider: '#E2E8F0',
          success: {
            main: '#22C55E',
            light: '#DCFCE7',
            dark: '#16A34A',
          },
          warning: {
            main: '#F59E0B',
            light: '#FEF3C7',
            dark: '#D97706',
          },
          error: {
            main: '#EF4444',
            light: '#FEE2E2',
            dark: '#DC2626',
          },
          info: {
            main: '#0D6EFD',
            light: '#EBF3FF',
            dark: '#0A58CA',
          },
          grey: {
            50: '#F5F7FA',
            100: '#F0F4F8',
            200: '#EBF3FF',
            300: '#E2E8F0',
            400: '#CBD5E1',
            500: '#9AAABB',
            600: '#5A6A85',
            700: '#0D1B3E',
            800: '#334155',
            900: '#0D1B3E',
          },
        }
      : {
          primary: {
            main: '#4D9FFF',       // lighter blue for dark mode
            light: '#1A2D50',
            dark: '#2A7AE8',
            contrastText: '#ffffff',
          },
          secondary: {
            main: '#00B4D8',
            light: '#0D2A35',
            dark: '#0096C7',
          },
          background: {
            default: '#0A0F1E',    // deep navy dark bg
            paper: '#111827',
            secondary: '#0D1525',
          },
          text: {
            primary: '#E2E8F0',
            secondary: '#8B9BB4',
            disabled: '#4A5568',
          },
          divider: '#1E2A3A',
          success: {
            main: '#22C55E',
            light: '#0F2A1A',
            dark: '#16A34A',
          },
          warning: {
            main: '#F59E0B',
            light: '#2A1F0A',
            dark: '#D97706',
          },
          error: {
            main: '#EF4444',
            light: '#2A0F0F',
            dark: '#DC2626',
          },
          info: {
            main: '#4D9FFF',
            light: '#0D1A30',
            dark: '#2A7AE8',
          },
          grey: {
            50: '#0A0F1E',
            100: '#0D1525',
            200: '#111827',
            300: '#1E2A3A',
            400: '#2D3A4E',
            500: '#4A5568',
            600: '#8B9BB4',
            700: '#E2E8F0',
            800: '#F0F4F8',
            900: '#F5F7FA',
          },
        }),
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
    h1: { fontSize: '2.25rem', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.02em' },
    h2: { fontSize: '1.875rem', fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.01em' },
    h3: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.3 },
    h4: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.35 },
    h5: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4 },
    h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.5 },
    subtitle1: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.5 },
    subtitle2: { fontSize: '0.8125rem', fontWeight: 500, lineHeight: 1.5 },
    body1: { fontSize: '0.875rem', lineHeight: 1.6, letterSpacing: '0.01em' },
    body2: { fontSize: '0.8125rem', lineHeight: 1.5, letterSpacing: '0.01em' },
    caption: { fontSize: '0.75rem', lineHeight: 1.4, letterSpacing: '0.02em' },
    button: { fontSize: '0.875rem', fontWeight: 600, textTransform: 'none' },
    overline: { fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' },
  },
  shape: { borderRadius: 8 },
  shadows: [
    'none',
    '0 1px 2px rgba(0,0,0,0.05)',
    '0 1px 3px rgba(0,0,0,0.08)',
    '0 2px 4px rgba(0,0,0,0.08)',
    '0 2px 8px rgba(0,0,0,0.1)',
    '0 4px 12px rgba(0,0,0,0.1)',
    '0 4px 16px rgba(0,0,0,0.12)',
    '0 8px 24px rgba(0,0,0,0.12)',
    '0 8px 32px rgba(0,0,0,0.14)',
    ...Array(16).fill('0 8px 32px rgba(0,0,0,0.14)'),
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: mode === 'light' ? '#D4E4FC #FAFAFA' : '#3A3F4E #0F1117',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': { width: 6, height: 6 },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 6,
            backgroundColor: mode === 'light' ? '#D4E4FC' : '#3A3F4E',
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
            backgroundColor: mode === 'light' ? '#9AA6B2' : '#555D6E',
          },
          '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          padding: '8px 16px',
          fontSize: '0.875rem',
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        contained: {
          backgroundColor: mode === 'light' ? '#0D6EFD' : '#4D9FFF',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: mode === 'light' ? '#0A58CA' : '#2A7AE8',
          },
        },
        outlined: {
          borderWidth: '1px',
          borderColor: mode === 'light' ? '#E2E8F0' : '#1E2A3A',
          color: mode === 'light' ? '#0D1B3E' : '#E2E8F0',
          '&:hover': {
            borderWidth: '1px',
            borderColor: mode === 'light' ? '#0D6EFD' : '#4D9FFF',
            backgroundColor: mode === 'light' ? '#EBF3FF' : '#1A2D50',
          },
        },
        text: {
          color: mode === 'light' ? '#0D6EFD' : '#4D9FFF',
          '&:hover': {
            backgroundColor: mode === 'light' ? '#EBF3FF' : '#1A2D50',
          },
        },
        sizeSmall: { padding: '6px 12px', fontSize: '0.8125rem' },
        sizeLarge: { padding: '10px 24px', fontSize: '0.9375rem' },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${mode === 'light' ? '#E2E8F0' : '#1E2A3A'}`,
          boxShadow: 'none',
          backgroundImage: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        elevation0: { boxShadow: 'none' },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          border: `1px solid ${mode === 'light' ? '#E2E8F0' : '#1E2A3A'}`,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: mode === 'light' ? '#FFFFFF' : '#111827',
          borderBottom: `1px solid ${mode === 'light' ? '#E2E8F0' : '#1E2A3A'}`,
          boxShadow: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: `1px solid ${mode === 'light' ? '#E2E8F0' : '#1E2A3A'}`,
          backgroundColor: mode === 'light' ? '#F0F4F8' : '#0D1525',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 6, fontWeight: 500, fontSize: '0.75rem', height: 24 },
        filled: { backgroundColor: mode === 'light' ? '#EBF3FF' : '#1A2D50' },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 8, fontSize: '0.875rem', border: '1px solid' },
        standardInfo: {
          backgroundColor: mode === 'light' ? '#EBF3FF' : '#0D1A30',
          borderColor: mode === 'light' ? '#0D6EFD' : '#4D9FFF',
          color: mode === 'light' ? '#0D1B3E' : '#E2E8F0',
        },
        standardSuccess: {
          backgroundColor: mode === 'light' ? '#DCFCE7' : '#0F2A1A',
          borderColor: mode === 'light' ? '#22C55E' : '#22C55E',
          color: mode === 'light' ? '#0D1B3E' : '#E2E8F0',
        },
        standardWarning: {
          backgroundColor: mode === 'light' ? '#FEF3C7' : '#2A1F0A',
          borderColor: mode === 'light' ? '#F59E0B' : '#F59E0B',
          color: mode === 'light' ? '#0D1B3E' : '#E2E8F0',
        },
        standardError: {
          backgroundColor: mode === 'light' ? '#FEE2E2' : '#2A0F0F',
          borderColor: mode === 'light' ? '#EF4444' : '#EF4444',
          color: mode === 'light' ? '#0D1B3E' : '#E2E8F0',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '20px',
          '&:last-child': { paddingBottom: '20px' },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingTop: '24px',
          paddingBottom: '24px',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${mode === 'light' ? '#E2E8F0' : '#1E2A3A'}`,
          fontSize: '0.875rem',
          padding: '12px 16px',
        },
        head: {
          fontWeight: 600,
          backgroundColor: mode === 'light' ? '#F0F4F8' : '#0D1525',
          color: mode === 'light' ? '#5A6A85' : '#8B9BB4',
          fontSize: '0.8125rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          padding: '10px 16px',
        },
        sizeSmall: {
          padding: '8px 12px',
          fontSize: '0.8125rem',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            fontSize: '0.875rem',
            '& fieldset': { borderColor: mode === 'light' ? '#E2E8F0' : '#1E2A3A' },
            '&:hover fieldset': { borderColor: mode === 'light' ? '#9AAABB' : '#4A5568' },
            '&.Mui-focused fieldset': {
              borderColor: mode === 'light' ? '#0D6EFD' : '#4D9FFF',
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          marginBottom: 2,
          '&.Mui-selected': {
            backgroundColor: mode === 'light' ? '#DBEAFE' : '#1A2D50',
            color: mode === 'light' ? '#0D6EFD' : '#4D9FFF',
            '&:hover': {
              backgroundColor: mode === 'light' ? '#BFDBFE' : '#1E3560',
            },
            '& .MuiListItemIcon-root': {
              color: mode === 'light' ? '#0D6EFD' : '#4D9FFF',
            },
          },
          '&:hover': {
            backgroundColor: mode === 'light' ? '#EBF3FF' : '#1A2D50',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: mode === 'light' ? '#0D1B3E' : '#E2E8F0',
          color: mode === 'light' ? '#ffffff' : '#0D1B3E',
          fontSize: '0.8125rem',
          padding: '6px 12px',
          borderRadius: 6,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 6,
          borderRadius: 3,
          backgroundColor: mode === 'light' ? '#EBF3FF' : '#1A2D50',
        },
        bar: { borderRadius: 3 },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: mode === 'light' ? '#E2E8F0' : '#1E2A3A' },
      },
    },
  },
});

export const CustomThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem('themeMode');
    return savedMode || 'light';
  });

  const toggleColorMode = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    localStorage.setItem('themeMode', newMode);
  };

  const theme = React.useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  return (
    <ThemeContext.Provider value={{ mode, toggleColorMode }}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
